
import React, { Suspense } from 'react';
import { RouteObject } from 'react-router-dom';
import ScrollToTop from '@/components/layout/ScrollToTop';
import { OpenLayout } from './layouts';
import { MobileRouteRedirect, DesktopRouteRedirect } from '@/components/routing/MobileRouteRedirect';

// Pages
import Index from '@/pages/Index';
import Home from '@/pages/Home';
import Login from '@/pages/Login';
import Signup from '@/pages/Signup';
import ForgotPassword from '@/pages/ForgotPassword';
import PasswordReset from '@/pages/PasswordReset';
import TryCalculator from '@/pages/TryCalculator';
import TryReversePiti from '@/pages/TryReversePiti';
import TryCalculatorLanding from '@/pages/TryCalculatorLanding';

import UserAgreement from '@/pages/UserAgreement';
import Brochure from '@/pages/Brochure';
import About from '@/pages/About';
import Legal from '@/pages/Legal';
import EULA from '@/pages/EULA';
import CookiePolicy from '@/pages/CookiePolicy';
import AccessDenied from '@/pages/AccessDenied';
import NotFound from '@/pages/NotFound';
import Logout from '@/pages/Logout';
import Compliance from '@/pages/Compliance';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import TermsOfService from '@/pages/TermsOfService';
import Disclaimers from '@/pages/Disclaimers';
import LegalHub from '@/pages/LegalHub';
import SharedReport from '@/pages/SharedReport';
import Widget from '@/pages/Widget';

// Freemium / public investor pages (lazy)
const InvestorAnalytics = React.lazy(() => import('@/pages/InvestorAnalytics'));
const MobileInvestorAnalytics = React.lazy(() => import('@/pages/MobileInvestorAnalytics'));
const MobileUnifiedCalculator = React.lazy(() => import('@/pages/MobileUnifiedCalculator'));
const InvestorAnalyticsReport = React.lazy(() => import('@/pages/InvestorAnalyticsReport'));

// Simple public layout without sidebar
const PublicPageLayout = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/40 to-background">
      <ScrollToTop />
      {children}
    </div>
  );
};


export const publicRoutes: RouteObject[] = [
  {
    path: '/',
    element: <Index />,
  },
  {
    path: '/home',
    element: (
      <PublicPageLayout>
        <Home />
      </PublicPageLayout>
    ),
  },
  {
    path: '/try',
    element: <TryCalculatorLanding />,
  },
  {
    path: '/try/payment',
    element: <TryCalculator />,
  },
  {
    path: '/try/reverse-piti',
    element: <TryReversePiti />,
  },
  {
    path: '/login',
    element: (
      <PublicPageLayout>
        <Login />
      </PublicPageLayout>
    ),
  },
  {
    path: '/signup',
    element: (
      <PublicPageLayout>
        <Signup />
      </PublicPageLayout>
    ),
  },
  {
    path: '/forgot-password',
    element: (
      <PublicPageLayout>
        <ForgotPassword />
      </PublicPageLayout>
    ),
  },
  {
    path: '/password-reset',
    element: (
      <PublicPageLayout>
        <PasswordReset />
      </PublicPageLayout>
    ),
  },
  {
    path: '/logout',
    element: <Logout />,
  },
  {
    path: '/agreement',
    element: (
      <PublicPageLayout>
        <UserAgreement />
      </PublicPageLayout>
    ),
  },
  {
    path: '/brochure',
    element: (
      <PublicPageLayout>
        <Brochure />
      </PublicPageLayout>
    ),
  },
  {
    path: '/about',
    element: (
      <PublicPageLayout>
        <About />
      </PublicPageLayout>
    ),
  },
  {
    path: '/legal',
    element: (
      <PublicPageLayout>
        <Legal />
      </PublicPageLayout>
    ),
  },
  {
    path: '/eula',
    element: (
      <PublicPageLayout>
        <EULA />
      </PublicPageLayout>
    ),
  },
  {
    path: '/cookie-policy',
    element: (
      <PublicPageLayout>
        <CookiePolicy />
      </PublicPageLayout>
    ),
  },
  {
    path: '/access-denied',
    element: (
      <PublicPageLayout>
        <AccessDenied />
      </PublicPageLayout>
    ),
  },
  {
    path: '/privacy',
    element: (
      <PublicPageLayout>
        <PrivacyPolicy />
      </PublicPageLayout>
    ),
  },
  {
    path: '/terms-of-service',
    element: (
      <PublicPageLayout>
        <TermsOfService />
      </PublicPageLayout>
    ),
  },
  {
    path: '/disclaimers',
    element: (
      <PublicPageLayout>
        <Disclaimers />
      </PublicPageLayout>
    ),
  },
  {
    path: '/legal-hub',
    element: (
      <PublicPageLayout>
        <LegalHub />
      </PublicPageLayout>
    ),
  },
  {
    path: '/compliance',
    element: (
      <PublicPageLayout>
        <Compliance />
      </PublicPageLayout>
    ),
  },
  {
    path: '/shared-report/:token',
    element: <SharedReport />,
  },
  {
    path: '/widget/:widgetId',
    element: <Widget />,
  },
  // Freemium investor features — publicly accessible (no auth required).
  {
    path: '/investor-analytics',
    element: (
      <OpenLayout>
        <MobileRouteRedirect mobileRoute="/mobile-investor-analytics">
          <InvestorAnalytics />
        </MobileRouteRedirect>
      </OpenLayout>
    ),
  },
  {
    path: '/mobile-investor-analytics',
    element: (
      <OpenLayout>
        <DesktopRouteRedirect desktopRoute="/investor-analytics">
          <MobileInvestorAnalytics />
        </DesktopRouteRedirect>
      </OpenLayout>
    ),
  },
  {
    path: '/mobile-unified-calculator',
    element: (
      <OpenLayout>
        <MobileUnifiedCalculator />
      </OpenLayout>
    ),
  },
  {
    path: '/investor-analytics-report',
    element: (
      <OpenLayout>
        <InvestorAnalyticsReport />
      </OpenLayout>
    ),
  },
];

